import { Button } from "@/components/elements/Button";
import { RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY } from "@/constants/common";
import { resendRequestResetPasswordApi } from "@/features/auth/services/resend-request-reset-password-api";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getInitialCooldownOnReload } from "@/utils/cooldown";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { toast } from "@/utils/toast";
import { createUrlWithParams } from "@/utils/url";
import { Paper, Stack, Typography, styled } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type TRecoverSuccessLocationState = {
  email: string;
};

export const RecoverSuccessForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateValue = location.state as TRecoverSuccessLocationState | null;
  const [isResending, setIsResending] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(() => getInitialCooldownOnReload(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY));

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const email = stateValue?.email ?? "";

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendCooldown > 0]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    const cooldownUntil = Date.now() + seconds * 1000;
    localStorage.setItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY, String(cooldownUntil));
    setResendCooldown(seconds);
  }, []);

  // Await logout BEFORE navigating so /login's withGuest doesn't race a stale
  // session (a still-logged-in admin) and bounce into the admin area. Carry the
  // recover email to /login?email=... so the form is pre-filled.
  const handleGoToLogin = useCallback(async () => {
    await logout();
    navigate(createUrlWithParams(AppRoutes.login, { email }), { replace: true });
  }, [navigate, email]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    try {
      await resendRequestResetPasswordApi({ email });
      toast.success("メールを再送しました。");
      startCooldown(60);
    } catch (error) {
      showError(error);
    } finally {
      setIsResending(false);
    }
  }, [email, resendCooldown, isResending, startCooldown]);

  return (
    <Container
      elevation={0}
      sx={{
        width: 464,
        p: 4,
        borderRadius: "16px",
      }}
    >
      <Stack gap={3}>
        <Typography variant="head20Bold" color={text.primary} textAlign="center">
          パスワード再発行メールの送信完了
        </Typography>
        <Typography fontSize="14px" color={text.secondary} mt="8px">
          パスワードの再発行メールをご登録されているメールアドレス宛に送信いたしました。
          <br />
          メールの内容に従ってパスワードを再発行してください。
        </Typography>
        <Stack gap={4} mt={3}>
          <Button size="sm" variant="primary" fullWidth onClick={handleGoToLogin}>
            <Typography variant="body14Semi">ログイン画面へ</Typography>
          </Button>
          <Stack alignItems="center" justifyContent="center">
            <Typography
              onClick={handleResend}
              variant="body14"
              color={resendCooldown > 0 || isResending ? "text.disabled" : text.link}
              sx={{
                cursor: resendCooldown > 0 || isResending ? "default" : "pointer",
                pointerEvents: resendCooldown > 0 || isResending ? "none" : "auto",
              }}
            >
              {resendCooldown > 0 ? `メールの再送信（${resendCooldown}秒）` : "メールの再送信（ご案内メールが届かない場合）"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

const Container = styled(Paper)`
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
